import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { X, Calendar, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Generate time slots from 9:00 to 18:00 in 30-minute intervals
const generateTimeSlots = () => {
  const slots = [];
  for (let hour = 9; hour < 18; hour++) {
    slots.push(`${hour.toString().padStart(2, '0')}:00`);
    slots.push(`${hour.toString().padStart(2, '0')}:30`);
  }
  slots.push('18:00');
  return slots;
};

const timeSlots = generateTimeSlots();

const financingTypes = [
  { value: 'immobilie', label: 'Immobilienfinanzierung' },
  { value: 'kredit', label: 'Kreditvergleich' },
  { value: 'geschaeftlich', label: 'Geschäftsfinanzierung' },
  { value: 'umschuldung', label: 'Umschuldung' },
  { value: 'sonstiges', label: 'Sonstiges' }
];

const BookingModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    date: '',
    time: '',
    financingType: '',
    consent: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  // Get minimum date (today)
  const today = new Date().toISOString().split('T')[0];

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name) {
      toast.error('Bitte geben Sie Ihren Namen ein.');
      return;
    }
    if (!formData.phone) {
      toast.error('Bitte geben Sie Ihre Telefonnummer ein.');
      return;
    }
    if (!formData.email) {
      toast.error('Bitte geben Sie Ihre E-Mail-Adresse ein.');
      return;
    }
    if (!formData.date) {
      toast.error('Bitte wählen Sie ein Datum aus.');
      return;
    }
    if (!formData.time) {
      toast.error('Bitte wählen Sie eine Uhrzeit aus.');
      return;
    }
    if (!formData.consent) {
      toast.error('Bitte stimmen Sie der Datenverarbeitung zu.');
      return;
    }

    setIsSubmitting(true);

    try {
      await axios.post(`${API_URL}/api/booking`, {
        name: formData.name,
        phone: formData.phone,
        email: formData.email,
        date: formData.date,
        time: formData.time,
        financing_type: formData.financingType || ''
      });

      setIsSuccess(true);
      toast.success('Terminanfrage erfolgreich gesendet!');
      
      // Reset after 3 seconds and close
      setTimeout(() => {
        setIsSuccess(false);
        setFormData({ name: '', phone: '', email: '', date: '', time: '', financingType: '', consent: false });
        onClose();
      }, 3000);

    } catch (error) {
      console.error('Error submitting booking request:', error);
      toast.error('Fehler beim Senden. Bitte versuchen Sie es erneut.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end lg:items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative w-full lg:w-auto lg:min-w-[500px] lg:max-w-lg bg-white rounded-t-3xl lg:rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 bg-slate-100 rounded-full flex items-center justify-center hover:bg-slate-200 transition-colors z-10"
        >
          <X className="w-5 h-5 text-slate-600" />
        </button>

        {isSuccess ? (
          /* Success State */
          <div className="p-8 text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-10 h-10 text-green-600" />
            </div>
            <h3 className="text-2xl font-bold text-slate-900 mb-3">
              Termin angefragt!
            </h3>
            <p className="text-slate-600">
              Ihr Termin wurde erfolgreich angefragt. Wir melden uns zur Bestätigung.
            </p>
          </div>
        ) : (
          /* Form State */
          <div className="p-6 lg:p-8">
            {/* Header */}
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <Calendar className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-slate-900">Beratungstermin buchen</h3>
                <p className="text-sm text-slate-500">Kostenlos und unverbindlich</p>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Name */}
              <div>
                <Label htmlFor="booking-name" className="text-slate-700">Name *</Label>
                <Input
                  id="booking-name"
                  placeholder="Ihr vollständiger Name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="mt-1.5"
                  required
                />
              </div>

              {/* Phone & Email in row */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="booking-phone" className="text-slate-700">Telefonnummer *</Label>
                  <Input
                    id="booking-phone"
                    type="tel"
                    placeholder="+49 123 456789"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="mt-1.5"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="booking-email" className="text-slate-700">E-Mail *</Label>
                  <Input
                    id="booking-email"
                    type="email"
                    placeholder="ihre@email.de"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="mt-1.5"
                    required
                  />
                </div>
              </div>

              {/* Date & Time in row */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="booking-date" className="text-slate-700">Gewünschtes Datum *</Label>
                  <Input
                    id="booking-date"
                    type="date"
                    min={today}
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    className="mt-1.5"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="booking-time" className="text-slate-700">Gewünschte Uhrzeit *</Label>
                  <Select 
                    value={formData.time} 
                    onValueChange={(value) => setFormData({ ...formData, time: value })}
                  >
                    <SelectTrigger className="mt-1.5">
                      <SelectValue placeholder="Uhrzeit wählen" />
                    </SelectTrigger>
                    <SelectContent>
                      {timeSlots.map((slot) => (
                        <SelectItem key={slot} value={slot}>{slot} Uhr</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Financing Type (Optional) */}
              <div>
                <Label htmlFor="booking-type" className="text-slate-700">Art der Finanzierung (optional)</Label>
                <Select 
                  value={formData.financingType} 
                  onValueChange={(value) => setFormData({ ...formData, financingType: value })}
                >
                  <SelectTrigger className="mt-1.5">
                    <SelectValue placeholder="Bitte wählen (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    {financingTypes.map((type) => (
                      <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Privacy Notice */}
              <p className="text-xs text-slate-500 bg-slate-50 p-3 rounded-lg">
                Ihre Daten werden vertraulich behandelt und ausschließlich zur Terminvereinbarung verwendet.
              </p>

              {/* Consent Checkbox */}
              <div className="flex items-start gap-3">
                <Checkbox
                  id="booking-consent"
                  checked={formData.consent}
                  onCheckedChange={(checked) => setFormData({ ...formData, consent: checked })}
                  className="mt-0.5"
                />
                <Label htmlFor="booking-consent" className="text-sm text-slate-600 cursor-pointer">
                  Ich stimme der Verarbeitung meiner Daten zu. *
                </Label>
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white shadow-lg py-6"
              >
                {isSubmitting ? (
                  <span className="flex items-center gap-2">
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Wird gesendet...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Calendar className="w-5 h-5" />
                    Termin jetzt verbindlich buchen
                  </span>
                )}
              </Button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default BookingModal;
